import React from 'react'
import { useSearchParams } from 'react-router-dom'

import { IConfirmResponse, ISignupRequestHoneypot } from '@interfaces/auth'
import { auth } from '@services/auth/auth-service'
import { handleApiError } from '@services/api'
import * as z from 'zod'
import toast from 'react-hot-toast'
import { Formik, FormikHelpers } from 'formik'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { Button, Divider, Input, Link } from '@heroui/react'
import { Icon } from '@iconify/react'
import { useNavigate } from 'react-router-dom'
import useAppState from '@/contexts/state'
import { AuthHeader } from '@/layouts/non-auth-layout'
import SocialSignIn from '@/components/auth/social-sign-in'
import { AnalyticsService } from '@/services/analytics'
import { EventCategory, EventName } from '@interfaces/events'
import { eventsService } from '@/services/analytics/events-service'
import { AxiosError } from 'axios'

const SignupSchema = z.object({
  firstName: z.string().min(1, 'First Name is required'),
  lastName: z.string().min(1, 'Last Name is required'),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Email is invalid')
    .refine(
      val => !val.includes('+'),
      'Please use your main email address. Aliases like name+tag@gmail.com aren’t supported.'
    ),
  password: z.string().min(8, 'Must be at least 8 characters').min(1, 'Password is required'),
})

const SignUp = () => {
  const [searchParams] = useSearchParams()
  const isGhostMode = searchParams.get('ghost') === 'true'
  const ghostSource = searchParams.get('ghostSource') as 'template' | 'non-template' | null

  document.title = isGhostMode ? 'Sign up' : 'Sign up to Cobrief'

  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false)

  const { setSession } = useAppState((state: any) => ({
    setSession: state.setSession,
  }))

  const navigate = useNavigate()

  const onSubmit = async (values: ISignupRequestHoneypot, { setSubmitting }: FormikHelpers<any>) => {
    try {
      setSubmitting(true)

      // Reject submission if a honeypot field is filled (likely a bot)
      if (values.name && values.name.trim() !== '') {
        setSubmitting(false)
        return
      }

      const { name, ...signupValues } = values // Strip honeypot values before API call
      const response = await auth.signUp(signupValues)
      const { user, session } = response.data as IConfirmResponse
      setSession(user, session)

      AnalyticsService.trackSignup()

      // fire signup_completed event
      eventsService.fireEvent({
        name: EventName.SIGNUP_COMPLETED,
        category: EventCategory.SIGNUP_CONVERSION,
        doc_public_id: undefined,
        source: isGhostMode ? 'GHOST' : 'SIGNUP_PAGE',
        meta: {},
      })

      if (isGhostMode) {
        // Send message to parent window
        window.parent.postMessage(
          {
            type: 'AUTH_SUCCESS',
            data: {
              user,
              session,
            },
          },
          '*'
        )
      } else {
        navigate('/')
      }
    } catch (e) {
      // axios error handling
      if (e instanceof AxiosError) {
        const message =
          e.response?.data?.error || e.response?.data?.message || 'Something went wrong. Please try again.'
        toast.error(message)
        return
      }

      // old code for backwards compatibility
      switch (handleApiError(e)) {
        case 'User already registered':
          toast.error('This email is already registered. Please login instead.')
          break
        case 'Blacklisted domain':
          toast.error('Please sign up using your business or work email address.')
          break
        default:
          toast.error('Something went wrong. Please try again.')
      }
    }
  }

  return (
    <React.Fragment>
      <AuthHeader
        title={
          !isGhostMode
            ? 'Start your 7-day free trial'
            : ghostSource === 'template'
              ? 'Create a free account or log in to customize this template.'
              : 'Create a free account or log in to keep drafting.'
        }
        subtitle={isGhostMode ? 'No credit card required.' : 'Access all our features. No credit card required'}
      />
      <SocialSignIn />

      <div className="flex items-center gap-4 py-2">
        <Divider className="flex-1" />
        <p className="shrink-0 text-tiny text-default-500">OR</p>
        <Divider className="flex-1" />
      </div>
      <Formik
        initialValues={{
          name: '',
          firstName: '',
          lastName: '',
          email: '',
          password: '',
        }}
        onSubmit={onSubmit}
        validationSchema={toFormikValidationSchema(SignupSchema)}
      >
        {({ errors, touched, handleSubmit, handleChange, isSubmitting, values }) => (
          <form className="flex flex-col gap-3" onSubmit={e => e.preventDefault()}>
            {/* Honeypot field hidden from real users */}
            <Input
              id="name"
              name="name"
              type="text"
              variant="bordered"
              value={values.name}
              onChange={handleChange}
              className="absolute left-0 top-0 z-[-1] h-0 w-0 opacity-0"
            />
            <Input
              id="firstName"
              label="First Name"
              name="firstName"
              placeholder="Enter your first name"
              type="name"
              variant="bordered"
              value={values.firstName}
              onChange={handleChange}
              isInvalid={errors?.firstName != null}
              errorMessage={touched?.firstName && errors?.firstName ? errors.firstName : ''}
            />
            <Input
              id="lastName"
              label="Last Name"
              name="lastName"
              placeholder="Enter your last name"
              type="name"
              variant="bordered"
              value={values.lastName}
              onChange={handleChange}
              isInvalid={errors?.lastName != null}
              errorMessage={touched?.lastName && errors?.lastName ? errors.lastName : ''}
            />
            <Input
              id="email"
              label="Email Address"
              name="email"
              placeholder="Enter your work email"
              type="email"
              variant="bordered"
              value={values.email}
              onChange={handleChange}
              isInvalid={errors?.email != null}
              errorMessage={touched?.email && errors?.email ? errors.email : ''}
            />
            <Input
              id="password"
              label="Password"
              name="password"
              placeholder="Enter your password"
              type={isPasswordVisible ? 'text' : 'password'}
              variant="bordered"
              value={values.password}
              onChange={handleChange}
              isInvalid={errors?.password != null}
              errorMessage={touched?.password && errors?.password ? errors.password : ''}
              endContent={
                <button type="button" onClick={() => setIsPasswordVisible(prev => !prev)}>
                  {isPasswordVisible ? (
                    <Icon className="pointer-events-none text-2xl text-default-400" icon="solar:eye-closed-linear" />
                  ) : (
                    <Icon className="pointer-events-none text-2xl text-default-400" icon="solar:eye-bold" />
                  )}
                </button>
              }
            />
            <div className="flex justify-center px-1 py-2 text-center">
              <p className="text-xs text-default-500">By signing up you agree to our Terms & Conditions</p>
            </div>
            <Button
              type="submit"
              isLoading={isSubmitting}
              onPress={() => handleSubmit()}
              style={{ backgroundColor: '#c6f648' }}
            >
              Sign Up
            </Button>
          </form>
        )}
      </Formik>
      <p className="text-center text-small text-default-500">
        Already have an account?&nbsp;
        <Link href={`/login${isGhostMode ? '?ghost=true' : ''}`} size="sm" style={{ color: '#000' }}>
          Log In
        </Link>
      </p>
    </React.Fragment>
  )
}

export default SignUp
